import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

// Broad-target landing for the head term "vaaterekki" (8 100 /mo, KDI 21).
// /lasten-vaaterekki stays as the exact-match page for "lasten vaaterekki".
const SLUG = "vaaterekki";
const H1 = "Vaaterekki lapselle — lasten oma vaateteline";
const TITLE = "Lasten vaaterekki lapsen korkeudella | Montessori24";
const DESC =
  "Lasten vaaterekki lapsen korkeudella: koivuvaneri, vakaa jalusta, tuotekohtaiset turvallisuustiedot. Ilmainen toimitus Suomeen 3–7 pv ja 30 pv palautusetu.";

const FAQS = [
  {
    q: "Mikä vaaterekki lapselle kannattaa valita?",
    a: "Lapselle sopivin vaaterekki on 90–120 cm korkea koivupuinen tai koivuvanerinen malli, jossa on leveä jalusta ja mielellään alahylly kengille. Aikuisten vaaterekki (esim. IKEA-, Jysk- tai Puuilo-mallit) on 150–170 cm korkea, joten lapsi ei yletä vaatteisiin itse — juuri se on Montessori-vaaterekin ydinajatus.",
  },
  {
    q: "Onko lapsen vaaterekki sama asia kuin vaateteline tai naulakko?",
    a: "Käytännössä kyllä. ‘Vaaterekki’, ‘vaateteline’ ja ‘naulakko’ tarkoittavat samaa: tanko henkareille + mahdollisia hyllyjä. Lapsen vaaterekki on vain mitoitettu taaperolle: matalampi tanko, tukevampi jalusta ja lapsen käyttöön turvalliset materiaalit.",
  },
  {
    q: "Minkä ikäiselle vaaterekki sopii?",
    a: "Käyttö alkaa yleensä 18 kk iässä, kun lapsi seisoo tukevasti. 2-vuotias osaa jo valita paidan ja housut itse; 3–4-vuotias hoitaa aamurutiinin täysin itsenäisesti. Sama rekki kestää esikouluikään asti.",
  },
  {
    q: "Kuinka paljon vaaterekki vie tilaa?",
    a: "Tyypillinen lasten vaaterekki on 60–80 cm leveä ja 100–120 cm korkea. Se mahtuu lastenhuoneen nurkkaan tai eteisen naulakon viereen. Alahylly kengille ja koreille säästää lattiatilaa entisestään.",
  },
];

export const Route = createFileRoute("/vaaterekki")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: VaaterekkiBroadPage,
});

function VaaterekkiBroadPage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Vaaterekki"
      title={H1}
      intro="Vaaterekki lapselle — samaan tapaan kuin aikuisten naulakko tai vaateteline, mutta taaperon korkeudella. Kun vaatteet ovat lapsen ulottuvilla, hän valitsee, pukee ja oppii itsenäisyyttä joka aamu."
      kauppaCategory="storage"
      match={(t) =>
        /vaate|vaaterekki|vaateteline|naulakko|clothing\s*rack|kids\s*rack|hanger\s*rack|clothes\s*rail/i.test(
          t,
        )
      }
      relatedChips={[
        { label: "Lasten vaaterekki", to: "/lasten-vaaterekki" },
        { label: "Lelulaatikko & säilytys", to: "/lelulaatikko" },
        { label: "Kaikki säilytys", to: "/kauppa" },
        { label: "Oppimistorni", to: "/oppimistorni" },
        { label: "Kalusteopas", to: "/montessori-koulu/montessori-kalusteet-kotiin" },
      ]}
      body={[
        {
          heading: "Vaaterekki vai naulakko — mitä eroa?",
          paragraphs: [
            "Vaaterekki, vaateteline ja seisova naulakko tarkoittavat kaikki samaa: pystymalli, jossa on tanko henkareille ja usein hylly ala- tai yläosassa. Lapsen vaaterekki eroaa aikuisen mallista vain mittasuhteissa: tanko on 90–120 cm korkeudella, jotta taapero ylettää vaatteisiinsa itse.",
            "IKEA-vaaterekki, Jysk-vaaterekki tai Puuilon vaaterekki ovat kaikki suunniteltu aikuisille — jos etsit lapselle sopivaa mallia, tarvitset matalamman ja tukevamman version. Meidän rekkimme on suunniteltu 1–6-vuotiaille: koivupuusta, leveällä jalustalla ja lapsen käyttöön turvallisilla pintakäsittelyillä.",
          ],
        },
        {
          heading: "Montessori-vaaterekki vai Jysk-vaaterekki — kumpi lapselle?",
          paragraphs: [
            "Jysk-vaaterekki (kuten monet IKEA- ja Puuilo-mallit) on suunniteltu aikuiselle: tanko 150–170 cm korkeudella ja kapea jalusta. Se on edullinen naulakko eteiseen, mutta lapsi ei yletä vaatteisiin ilman aikuisen apua — ja kevyt metallirunko kaatuu helposti, jos taapero tarttuu tankoon.",
            "Lapsen Montessori-vaaterekki on mitoitettu käyttäjälleen: tanko 90–120 cm korkeudella, leveä kaatumista estävä jalusta ja koivua tai koivuvaneria. Hintaa on enemmän kuin Jyskin perusrekissä, mutta sama kaluste palvelee vuosia 1–6-vuotiaasta esikouluikään — ja tekee pukemisesta lapsen itsenäisen rutiinin.",
          ],
        },
        {
          heading: "Miksi lapsen oma vaaterekki muuttaa aamun",
          paragraphs: [
            "Aikuisen kaappi on lapselle mahdoton: naulakot 150 cm korkealla, ovet raskaita ja vaatteet aikuisen valitsemia. Lapsen kokoinen vaaterekki laittaa 5–8 asua taaperon ulottuville — sopiva määrä, jonka joukosta lapsi osaa valita ilman ylikuormitusta.",
            "Käytännön vaikutus: aamu ei ole tappelua siitä mitä puetaan. Lapsi valitsee, pukee ja on ylpeä. Sivuvaikutuksena hienomotoriikka kehittyy nopeammin — nappien ja vetoketjujen käyttö on jokapäiväinen harjoitus.",
          ],
        },
        {
          heading: "Mihin kiinnittää huomiota lasten vaaterekkiä ostaessa",
          paragraphs: [
            "Vakaus: leveä alusta tai poikittainen jalkojen välinen tuki estävät kaatumisen kun lapsi vetää vaatetta pois henkarilta.",
            "Korkeus: tangon on oltava lapsen olkapäätä matalampi — muuten hän ei saa vaatetta pois henkarista. Kasvavalle lapselle säädettävä korkeus on plussaa.",
            "Materiaali: koivupuu tai -vaneri kestää lasten käyttöä vuosia. Metallirekit ovat kylmiä ja usein epävakaita. Pintakäsittelyn tulee olla lapsiturvallinen (EN71).",
            "Alahylly: kengille, koreille tai laatikoille — sama kaluste hoitaa siten sekä vaatteet että pikkutavarat.",
          ],
        },
      ]}
      faqs={FAQS}
    />
  );
}
